import { useEffect, useRef, useState, useCallback } from "react";
import JMuxer from "../lib/jmuxer";
import {
  extractViewingStreamUrl,
  relayProtocolFromEndpoint,
} from "../lib/startViewingCommand";

const { ViewerCoordinator, ViewerSession, G711AudioSink, boundedStatus } = require("../lib/rtsp");
const { buildStartViewingRequest, buildStopViewingRequest, validateStreamId } = require("../lib/signaling");

const TARGET_LEAD_SECONDS = 0.4;
const GENTLE_LEAD_SECONDS = 0.8;
const MAX_LEAD_SECONDS = 2;
const MIN_LIVE_AUDIO_DELAY_SECONDS = 0.75;
const CATCH_UP_RATE = 1.08;
const HEVC_MIME = 'video/mp4; codecs="hvc1.1.6.L93.B0"';

function hevcSupported(mediaSource = window.MediaSource) {
  return typeof mediaSource?.isTypeSupported === "function" && mediaSource.isTypeSupported(HEVC_MIME);
}

function playbackCorrection(bufferedEnd, currentTime) {
  if (!Number.isFinite(bufferedEnd) || !Number.isFinite(currentTime)) return { playbackRate: 1, seekTo: null };
  const lead = bufferedEnd - currentTime;
  if (lead > MAX_LEAD_SECONDS) return { playbackRate: 1, seekTo: bufferedEnd - TARGET_LEAD_SECONDS };
  if (lead > GENTLE_LEAD_SECONDS) return { playbackRate: CATCH_UP_RATE, seekTo: null };
  return { playbackRate: 1, seekTo: null };
}

function liveAudioDelay(bufferedEnd, currentTime) {
  if (!Number.isFinite(bufferedEnd) || !Number.isFinite(currentTime)) return MIN_LIVE_AUDIO_DELAY_SECONDS;
  const lead = bufferedEnd - currentTime;
  if (lead <= 0) return MIN_LIVE_AUDIO_DELAY_SECONDS;
  return Math.max(MIN_LIVE_AUDIO_DELAY_SECONDS, Math.min(lead, MAX_LEAD_SECONDS));
}

function waitForWebSocketOpen(ws, timeoutMs = 10000) {
  if (ws.readyState === WebSocket.OPEN) return Promise.resolve();
  if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
    return Promise.reject(new Error("Viewer WebSocket closed"));
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("Viewer WebSocket connection timed out"));
    }, timeoutMs);

    const onOpen = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("Viewer WebSocket connection failed"));
    };

    const cleanup = () => {
      clearTimeout(timer);
      ws.removeEventListener("open", onOpen);
      ws.removeEventListener("error", onError);
    };

    ws.addEventListener("open", onOpen);
    ws.addEventListener("error", onError);
  });
}

function isDirectViewerEndpoint(endpoint) {
  return typeof endpoint === "string" && /\/liveview\/viewer\/ws\//i.test(endpoint);
}

/**
 * Manages a single liveview WebSocket session for one camera cell.
 *
 * Playback flow:
 *   1. executeSurveillanceCommand (startViewing) → viewer WS URL from API
 *   2. Connect to that URL and run the same viewer signaling as before
 */
export function useLiveview({
  videoRef,
  falconEndpoint,
  relayProtocol,
  deviceId,
  deviceSiteId,
  accessToken,
  streamId = 1,
  requestStartViewingCommand,
  enabled = true,
  muted = false,
}) {
  const [status, setStatusRaw] = useState("idle");
  const rpcRef = useRef(null);
  const coordinatorRef = useRef(null);
  const audioSinkRef = useRef(null);
  const connectedEndpointRef = useRef("");
  const teardownRef = useRef(null);

  const mutedRef = useRef(muted);
  const unlockAudio = useCallback(() => {
    const result = audioSinkRef.current?.unlock?.();
    if (result && typeof result.then === "function") {
      return result.catch(() => null);
    }
    return Promise.resolve(null);
  }, []);

  useEffect(() => {
    mutedRef.current = muted;
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
    if (!muted) {
      unlockAudio();
    } else {
      audioSinkRef.current?.reset();
    }
  }, [muted, videoRef, unlockAudio]);

  const setStatus = useCallback((msg) => setStatusRaw(boundedStatus(msg)), []);
  const retryCleanupRef = useRef(null);

  const clearRetryLoop = useCallback(() => {
    retryCleanupRef.current?.();
    retryCleanupRef.current = null;
  }, []);

  const teardown = useCallback(() => {
    clearRetryLoop();
    teardownRef.current?.();
    teardownRef.current = null;
    connectedEndpointRef.current = "";
    rpcRef.current = null;
    coordinatorRef.current = null;
    audioSinkRef.current = null;
  }, [clearRetryLoop]);

  const connectViewer = useCallback(
    (viewerEndpoint, effectiveRelayProtocol) => {
      if (!viewerEndpoint || !deviceId || !accessToken) return;

      if (connectedEndpointRef.current === viewerEndpoint && rpcRef.current) {
        return;
      }

      const preparedAudioSink = !rpcRef.current ? audioSinkRef.current : null;
      teardown();

      const audioSink = preparedAudioSink || new G711AudioSink(() => new (window.AudioContext || window.webkitAudioContext)());
      audioSinkRef.current = audioSink;
      connectedEndpointRef.current = viewerEndpoint;

      const createSession = ({ socket, onStop }) => {
        const videoEl = videoRef.current;
        const createMuxer = (muxerConfig = {}) => {
          if (!videoEl) return { feed() {}, destroy() {} };
          return new JMuxer({
            node: videoEl,
            mode: "video",
            flushingTime: 0,
            fps: Number.isFinite(muxerConfig.fps) && muxerConfig.fps > 0 ? muxerConfig.fps : 30,
            maxDelay: 10000,
            debug: false,
            videoCodec: muxerConfig.videoCodec || "H264",
          });
        };

        let muxer = createMuxer();

        if (videoEl) {
          videoEl.ontimeupdate = () => {
            if (!videoEl.buffered.length) return;
            const { playbackRate, seekTo } = playbackCorrection(
              videoEl.buffered.end(videoEl.buffered.length - 1),
              videoEl.currentTime
            );
            if (seekTo !== null) videoEl.currentTime = seekTo;
            if (videoEl.playbackRate !== playbackRate) videoEl.playbackRate = playbackRate;
          };
        }

        return new ViewerSession({
          socket,
          worker: (() => {
            const mediaWorker = new Worker(`${process.env.PUBLIC_URL || ""}/rtp-worker.js`);
            mediaWorker.onerror = () => {
              setStatus("Error: media worker failed to load");
            };
            return mediaWorker;
          })(),
          audioSink,
          onConfigChanged: (message) => {
            muxer.destroy?.();
            if (message?.videoCodec === "H265" && !hevcSupported()) {
              muxer = { feed() {}, destroy() {} };
              setStatus("Error: this browser cannot decode H.265");
              return true;
            }
            muxer = createMuxer(message || {});
            videoEl?.play?.().catch(() => {});
          },
          onVideo: ({ buffer, duration }) => {
            try {
              muxer.feed(duration ? { video: buffer, duration } : { video: buffer });
            } catch (_err) {
              // Ignore transient MSE errors during remuxer rebuild
            }
          },
          onAudio: ({ buffer, codec, timestamp }) => {
            if (!videoEl || mutedRef.current) return;
            const bufferedEnd = videoEl.buffered.length
              ? videoEl.buffered.end(videoEl.buffered.length - 1)
              : null;
            const delay = liveAudioDelay(bufferedEnd, videoEl.currentTime);
            if (delay !== null) audioSink.feed(buffer, codec, delay, videoEl.playbackRate, timestamp);
          },
          onClose: () => muxer.destroy?.(),
          setStatus,
          onStop,
        });
      };

      if (isDirectViewerEndpoint(viewerEndpoint)) {
        const directSocket = new WebSocket(viewerEndpoint);
        directSocket.binaryType = "arraybuffer";
        rpcRef.current = directSocket;

        const session = createSession({
          socket: directSocket,
          onStop: () => {
            if (coordinatorRef.current?.viewer === session) {
              coordinatorRef.current.viewer = null;
            }
          },
        });

        coordinatorRef.current = {
          viewer: session,
          sessionId: null,
          beginStart: () => 1,
          stopActive: () => {
            if (coordinatorRef.current?.viewer === session) {
              session.stop();
              coordinatorRef.current.viewer = null;
              coordinatorRef.current.sessionId = null;
            }
          },
        };

        directSocket.onopen = () => { if (coordinatorRef.current?.viewer === session) session.open(); };
        directSocket.onmessage = (event) => {
          if (coordinatorRef.current?.viewer !== session) return;
          typeof event.data === "string" ? session.onText(event.data) : session.onBinary(event.data);
        };
        directSocket.onclose = () => session.close();
        directSocket.onerror = () => setStatus("Error: viewer connection failed");

        teardownRef.current = () => {
          directSocket.close();
          session.stop();
          audioSink.close?.();
        };
        return;
      }

      const rpc = new WebSocket(viewerEndpoint);
      rpcRef.current = rpc;
      const coordinator = new ViewerCoordinator({
        relayProtocol: effectiveRelayProtocol,
        createSocket: ({ url }) => {
          const ws = new WebSocket(url);
          ws.binaryType = "arraybuffer";
          return ws;
        },
        createSession,
        sendStop: (sessionId) => rpc.send(JSON.stringify(buildStopViewingRequest({
          requestId: `web-stop-${sessionId}`,
          deviceId,
          accessToken,
          sessionId,
        }))),
        onError: () => setStatus("Error: relay connection failed"),
      });
      coordinatorRef.current = coordinator;

      rpc.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const relay = message.result?.relay || message.result?.data?.relay;
          if (relay) {
            const generation = Number(String(message.id || "").replace("web-start-", ""));
            coordinator.acceptStart(generation, relay);
          }
        } catch {
          setStatus("Error: invalid controller response");
        }
      };
      rpc.onerror = () => setStatus("Error: controller connection failed");

      teardownRef.current = () => {
        rpc.close();
        coordinator.stopActive();
        audioSink.close?.();
      };
    },
    [deviceId, accessToken, videoRef, setStatus, teardown]
  );

  useEffect(() => teardown, [deviceId, accessToken, streamId, teardown]);

  const resolveViewerEndpoint = useCallback(
    async (streamIdValue) => {
      if (requestStartViewingCommand) {
        setStatus(`stream ${streamIdValue} · command…`);
        const response = await requestStartViewingCommand({
          deviceId,
          streamId: streamIdValue,
          siteId: deviceSiteId,
        });
        const viewerUrl = extractViewingStreamUrl(response);
        if (!viewerUrl) {
          throw new Error("startViewing response missing viewer WebSocket URL");
        }
        return viewerUrl;
      }

      if (!falconEndpoint) {
        throw new Error("No viewer endpoint configured");
      }
      return falconEndpoint;
    },
    [requestStartViewingCommand, deviceId, deviceSiteId, falconEndpoint, setStatus]
  );

  const start = useCallback(async () => {
    if (!enabled || !deviceId || !accessToken) return false;

    try {
      const id = validateStreamId(streamId);
      if (!audioSinkRef.current && !rpcRef.current && (window.AudioContext || window.webkitAudioContext)) {
        audioSinkRef.current = new G711AudioSink(() => new (window.AudioContext || window.webkitAudioContext)());
        await unlockAudio();
      }
      const viewerEndpoint = await resolveViewerEndpoint(id);
      const effectiveRelayProtocol = requestStartViewingCommand
        ? relayProtocolFromEndpoint(viewerEndpoint)
        : relayProtocol || relayProtocolFromEndpoint(viewerEndpoint);

      connectViewer(viewerEndpoint, effectiveRelayProtocol);

      const rpc = rpcRef.current;
      const coordinator = coordinatorRef.current;
      if (!rpc || !coordinator) return false;

      const directViewer = isDirectViewerEndpoint(viewerEndpoint);
      if (directViewer) {
        await waitForWebSocketOpen(rpc);
        await unlockAudio();
        setStatus(`stream ${id} · connected…`);
        return true;
      }

      await waitForWebSocketOpen(rpc);

      await unlockAudio();
      setStatus(`stream ${id} · connecting…`);
      const generation = coordinator.beginStart();
      rpc.send(JSON.stringify(buildStartViewingRequest({
        requestId: `web-start-${generation}`,
        deviceId,
        accessToken,
        streamId: id,
      })));
      return true;
    } catch (err) {
      setStatus(`Error: ${err.message}`);
      return false;
    }
  }, [
    deviceId,
    accessToken,
    streamId,
    setStatus,
    resolveViewerEndpoint,
    connectViewer,
    relayProtocol,
    requestStartViewingCommand,
    enabled,
    unlockAudio,
  ]);

  const startWhenReady = useCallback(() => {
    clearRetryLoop();
    const cleanup = () => {
      if (retryCleanupRef.current === cleanup) {
        retryCleanupRef.current = null;
      }
    };

    retryCleanupRef.current = cleanup;
    start().then((ok) => {
      if (!ok) {
        retryCleanupRef.current = null;
      }
    });

    return cleanup;
  }, [start, clearRetryLoop]);

  const pause = useCallback(() => {
    clearRetryLoop();
    coordinatorRef.current?.stopActive();
    setStatus("paused");
  }, [clearRetryLoop, setStatus]);
  const resume = useCallback(() => {
    coordinatorRef.current?.viewer?.resume();
    setStatus("resumed");
  }, [setStatus]);
  const stop = useCallback(() => {
    clearRetryLoop();
    coordinatorRef.current?.stopActive();
    teardown();
    setStatus("idle");
  }, [clearRetryLoop, teardown, setStatus]);

  return { status, start: startWhenReady, pause, resume, stop };
}
